/**
 * Curated fixtures for the prototype shell — the few areas where generic faker data isn't
 * good enough and the product team needs realistic, coherent content:
 *
 *   - Reports: the real report catalog (every ReportName, grouped by category) + populated rows.
 *   - Schedule: perpetual, week-to-week recurring classes so every week shows a full timetable.
 *
 * Each generator returns a fully-shaped object/array (all documented fields of its type) so the
 * GQty selection set is satisfied. Generators are deterministic (seeded by a stable key) so the
 * same week / same report renders identically across refetches.
 */
import { faker } from '@faker-js/faker'
import { ReportName } from '../gqty/schema.generated'

function hashString(str) {
  let h = 5381
  for (let i = 0; i < String(str).length; i++) h = (h * 33) ^ String(str).charCodeAt(i)
  return h >>> 0
}
const seed = (key) => faker.seed(hashString(key))
const pick = (arr, i) => arr[((i % arr.length) + arr.length) % arr.length]
const titleCase = (s) =>
  String(s)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
const readableDate = (d) =>
  d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

// ---------------------------------------------------------------------------
// SCHEDULE — perpetual recurring classes within the requested date range
// ---------------------------------------------------------------------------
const CLASS_NAMES = [
  'Reformer Flow',
  'HIIT Circuit',
  'Vinyasa Yoga',
  'Spin',
  'Strength & Conditioning',
  'Boxing',
  'Pilates Mat',
  'Mobility & Stretch',
]
const INSTRUCTORS = [
  'Ava Mitchell',
  'Liam Brooks',
  'Sophia Chen',
  'Noah Patel',
  'Emma Rossi',
  'Mason Clark',
]
const ROOMS = ['Studio A', 'Studio B', 'Main Floor', 'Reformer Room']
const COLORS = ['#00a2e1', '#3a640d', '#156572', '#3d208d', '#f5a623', '#c0392b']
const CLASS_TIMES = [
  [6, 0],
  [9, 0],
  [12, 0],
  [17, 0],
  [18, 30],
]

function parseDate(v, fallback) {
  if (!v) return fallback
  const d = new Date(String(v).length <= 10 ? `${v}T00:00:00` : v)
  return isNaN(d.getTime()) ? fallback : d
}

function startOfWeek(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)) // Monday
  return x
}

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function dayRange(args) {
  const now = new Date()
  const start = parseDate(args.startDate ?? args.dateFrom, startOfWeek(now))
  start.setHours(0, 0, 0, 0)
  const end =
    parseDate(args.endDate ?? args.dateTo, null) || new Date(start.getTime() + 6 * 86400000)
  const days = []
  const d = new Date(start)
  while (d <= end && days.length < 21) {
    days.push(new Date(d))
    d.setDate(d.getDate() + 1)
  }
  return days
}

function sessionsForDay(day, baseIdx) {
  const now = new Date()
  seed(`sess:${day.toDateString()}`)
  return CLASS_TIMES.map(([h, m], j) => {
    const idx = baseIdx + j
    const s = new Date(day)
    s.setHours(h, m, 0, 0)
    const e = new Date(s)
    e.setMinutes(e.getMinutes() + 50)
    const name = pick(CLASS_NAMES, idx)
    const size = 12 + (idx % 3) * 4
    const booked = (idx * 7) % (size + 1)
    return {
      __typename: 'CalendarSession',
      sessionId: `proto-session-${s.toISOString()}`,
      name,
      description: `${name} class`,
      // The schedule parses times via Number(startTime) → epoch milliseconds, not ISO.
      startTime: String(s.getTime()),
      endTime: String(e.getTime()),
      status: e < now ? 'COMPLETED' : 'SCHEDULED',
      size,
      bookedCount: booked,
      waitlistCount: booked >= size ? idx % 4 : 0,
      waitlistEnabled: true,
      isBlockSlot: false,
      roomName: pick(ROOMS, idx),
      sessionTypeColor: pick(COLORS, idx),
      siteLocationRoomId: `proto-room-${idx % ROOMS.length}`,
      staffUserIds: [`proto-staff-${idx % INSTRUCTORS.length}`],
      instructor: [
        {
          __typename: 'CalendarSessionInstructor',
          name: pick(INSTRUCTORS, idx),
          staffUserId: `proto-staff-${idx % INSTRUCTORS.length}`,
        },
      ],
      bookings: [],
    }
  })
}

// Flat list of CalendarSession across the range (day view / calendarSessions / sessions).
function genCalendarSessions(args) {
  const days = dayRange(args)
  let idx = 0
  const all = []
  for (const day of days) {
    const s = sessionsForDay(day, idx)
    idx += s.length
    all.push(...s)
  }
  return all
}

// Grouped-by-day shape for the week grid (weekViewSessions -> [WeekViewDay!]!).
function genWeekViewSessions(args) {
  const days = dayRange(args)
  let idx = 0
  return days.map((day) => {
    const s = sessionsForDay(day, idx)
    idx += s.length
    return {
      __typename: 'WeekViewDay',
      date: ymd(day),
      sessions: s,
      overflowSessions: [],
      totalCount: s.length,
    }
  })
}

// ---------------------------------------------------------------------------
// REPORTS — real catalog + populated rows
// ---------------------------------------------------------------------------
function reportCategory(name) {
  if (/payroll|staff|utilisation|one_to_one/.test(name)) return 'staff'
  if (
    /invoice|revenue|payment|refund|settlement|delinquen|amount_due|royalty|income|pos_history|gift_card|credit_balance|failed|leakage|stripe|ezypay|sales_by|discount|promo/.test(
      name
    )
  )
    return 'financial'
  return 'client_and_attendance'
}

const COLUMNS_BY_CATEGORY = {
  client_and_attendance: [
    { key: 'clientName', label: 'Client' },
    { key: 'email', label: 'Email' },
    { key: 'membership', label: 'Membership' },
    { key: 'visits', label: 'Visits' },
    { key: 'lastVisit', label: 'Last Visit' },
    { key: 'status', label: 'Status' },
  ],
  financial: [
    { key: 'clientName', label: 'Client' },
    { key: 'invoiceNumber', label: 'Invoice #' },
    { key: 'date', label: 'Date' },
    { key: 'amount', label: 'Amount' },
    { key: 'paymentMethod', label: 'Method' },
    { key: 'status', label: 'Status' },
  ],
  staff: [
    { key: 'staffName', label: 'Staff' },
    { key: 'role', label: 'Role' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'hours', label: 'Hours' },
    { key: 'utilisation', label: 'Utilisation' },
  ],
}

function toColumns(category) {
  return COLUMNS_BY_CATEGORY[category].map((c) => ({
    __typename: 'ReportDefinitionColumns',
    key: c.key,
    label: c.label,
    format: null,
    defaultVisible: true,
    isRedacted: false,
  }))
}

function toReportDefinition(name) {
  const category = reportCategory(name)
  return {
    __typename: 'ReportDefinition',
    name,
    label: titleCase(name),
    description: `${titleCase(name)} — overview of ${category.replace(/_/g, ' ')} activity.`,
    columns: toColumns(category),
    filters: [],
    permissionCategory: category,
    dateRangeDirection: 'PAST',
    disablePast: false,
    isAggregated: false,
    locked: false,
    requiresDateRange: true,
  }
}

// Stable catalog of every real report, grouped by category.
const REPORT_CATALOG = Object.values(ReportName).map(toReportDefinition)
const REPORT_BY_NAME = Object.fromEntries(REPORT_CATALOG.map((r) => [r.name, r]))

function genAvailableReports() {
  return { __typename: 'AvailableReports', reports: REPORT_CATALOG }
}

function genReportDefinition(args) {
  const name = args?.input?.reportName ?? args?.reportName
  return REPORT_BY_NAME[name] || toReportDefinition(name || 'all_clients')
}

const MEMBERSHIPS = ['Unlimited Monthly', '10-Class Pack', 'Annual', 'Drop-in', 'Intro Offer']
const METHODS = ['Visa ••4242', 'Mastercard ••5100', 'Amex ••0005', 'Account Credit', 'Cash']
const ROLES = ['Instructor', 'Front Desk', 'Manager', 'Personal Trainer']
const STATUSES = ['Active', 'Paid', 'Completed', 'Pending', 'Cancelled']

function cellValue(key, i) {
  const k = key.toLowerCase()
  if (k.includes('email')) return faker.internet.email()
  if (k.includes('last') && k.includes('visit'))
    return readableDate(faker.date.recent({ days: 30 }))
  if (k.includes('date')) return readableDate(faker.date.recent({ days: 90 }))
  if (k.includes('name') || k === 'client' || k === 'staff') return faker.person.fullName()
  if (k.includes('membership') || k.includes('plan') || k.includes('package'))
    return pick(MEMBERSHIPS, i)
  if (k.includes('method')) return pick(METHODS, i)
  if (k.includes('role')) return pick(ROLES, i)
  if (k.includes('invoice')) return `INV-${String(1000 + i)}`
  if (k.includes('amount') || k.includes('revenue') || k.includes('price') || k.includes('total'))
    return `$${faker.number.float({ min: 10, max: 850, fractionDigits: 2 }).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  if (k.includes('utilis') || k.includes('rate') || k.includes('percent'))
    return `${faker.number.int({ min: 35, max: 98 })}%`
  if (k.includes('status')) return pick(STATUSES, i)
  if (k.includes('visit') || k.includes('session') || k.includes('count'))
    return faker.number.int({ min: 1, max: 60 })
  if (k.includes('hour')) return faker.number.int({ min: 2, max: 40 })
  return faker.lorem.word()
}

function genRunReport(args) {
  const name = args?.input?.reportName ?? args?.reportName ?? 'all_clients'
  const pageSize = Math.min(Number(args?.input?.pageSize) || 25, 50)
  const def = REPORT_BY_NAME[name] || toReportDefinition(name)
  seed(`report:${name}`)
  const results = Array.from({ length: pageSize }, (_, i) => {
    const row = {}
    for (const col of def.columns) row[col.key] = cellValue(col.key, i)
    return { __typename: 'RunReportResults', cursor: `c${i}`, row }
  })
  return {
    __typename: 'RunReport',
    reportName: name,
    columns: def.columns,
    results,
    totalsRow: null,
    isTruncated: false,
    maxExportRows: 5000,
    piiRedactionApplied: false,
    redactedColumnKeys: [],
    pageInfo: {
      __typename: 'RunReportPageInfo',
      startCursor: 'c0',
      endCursor: `c${results.length - 1}`,
      hasNextPage: false,
      totalRecords: 120 + (hashString(name) % 400),
    },
  }
}

// ---------------------------------------------------------------------------
// CART CALCULATION — real, consistent totals computed from the cart input items
// (all CalculateCart fields are String!, and the UI parseFloat()s them, so the
// generic word-values become NaN/0 — hence "$ NaN" / "$ 0.00" totals).
// ---------------------------------------------------------------------------
const round2 = (n) => Math.round(n * 100) / 100
const money = (n) => String(round2(n))
const toNum = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

function genCalculateCart(args) {
  const input = Array.isArray(args.items) ? args.items : []
  const RATE = 9 // flat % tax for items with tax enabled
  let subtotal = 0
  let totalDiscount = 0
  let totalTax = 0
  let total = 0
  const items = input.map((it, i) => {
    const qty = toNum(it.quantity) || 1
    const unit = toNum(it.unitPrice)
    const totalValue = unit * qty
    let discountAmount = 0
    const dv = toNum(it.discountValue)
    if (dv)
      discountAmount =
        it.discountType === 'percentage' || it.discountType === '%' ? (totalValue * dv) / 100 : dv
    discountAmount = Math.min(discountAmount, totalValue)
    const preTax = totalValue - discountAmount
    const taxed = Array.isArray(it.taxSettingIds) && it.taxSettingIds.length > 0
    const exclTax = taxed ? (preTax * RATE) / 100 : 0
    const itemTotal = preTax + exclTax
    subtotal += totalValue
    totalDiscount += discountAmount
    totalTax += exclTax
    total += itemTotal
    return {
      itemId: it.itemId ?? `item-${i}`,
      quantity: qty,
      unitPrice: money(unit),
      totalValue: money(totalValue),
      discountAmount: money(discountAmount),
      preTaxAmount: money(preTax),
      totalAmount: money(itemTotal),
      exclusiveTaxAmount: money(exclTax),
      inclusiveTaxAmount: money(0),
      totalBillingCycles: null,
      secondaryRecurringFeeTotalBillingCycles: null,
      proratedAmount: null,
      proratedDays: null,
      prorationStartDate: null,
      prorationEndDate: null,
      proratedCredits: null,
      additionalCreditsAmount: null,
      secondaryProratedAmount: null,
      proratedTotalAmount: null,
      taxes: taxed
        ? [
            {
              taxSettingId: 'gst',
              name: 'GST',
              rate: String(RATE),
              type: 'exclusive',
              amount: money(exclTax),
            },
          ]
        : [],
      secondaryRecurringFeeTaxes: [],
    }
  })
  return {
    subtotal: money(subtotal),
    totalDiscount: money(totalDiscount),
    preTaxTotal: money(subtotal - totalDiscount),
    totalExclusiveTax: money(totalTax),
    totalInclusiveTax: money(0),
    totalTax: money(totalTax),
    totalAmount: money(total),
    dueToday: money(total),
    futurePayments: money(0),
    secondaryRecurringFeeFuturePayments: money(0),
    taxBreakdown:
      totalTax > 0
        ? [
            {
              taxSettingId: 'gst',
              name: 'GST',
              rate: String(RATE),
              type: 'exclusive',
              totalAmount: money(totalTax),
            },
          ]
        : [],
    items,
  }
}

// CONFIRM PAYMENT — createPayments must return status 'Success' (the PaymentStatus enum's
// first value is 'Canceled', so the generic mock never triggers the success/navigate path).
function genCreatePayments(args) {
  const inputs = Array.isArray(args.input) ? args.input : []
  const amount = round2(inputs.reduce((s, i) => s + toNum(i.amount), 0)) || 100
  const now = new Date().toISOString()
  return [
    {
      __typename: 'Payment',
      paymentId: 'proto-payment-1',
      clientId: 'proto-client',
      siteId: args.siteId ?? 'proto-site',
      invoiceId: args.invoiceId ?? 'proto-invoice',
      paymentMethodType: 'Card',
      clientPaymentMethodId: 'proto-pm',
      currency: 'USD',
      amount,
      status: 'Success',
      idempotencyKey: 'proto-idem-1',
      createdAt: now,
      updatedAt: now,
      succeedAt: now,
      isAutoRetryable: false,
      isClientActionRequired: false,
      autoRetryAttempts: 0,
      transactions: [
        {
          __typename: 'PaymentGatewayTransaction',
          paymentGatewayTransactionId: 'proto-txn-1',
          status: 'Success',
          amount,
          failureCategory: null,
          rawErrorMessage: null,
        },
      ],
    },
  ]
}

// DASHBOARD KPIs — the generic resolver leaves these null (so the UI shows 0 / $0).
// Curate believable, deterministic numbers, seeded per location + date range so the cards
// stay stable across reloads and differ sensibly between brand and location views.
function genDashboardKpis(args) {
  const input = args?.input || {}
  seed(
    `dashboard-kpis:${input.locationId || 'brand'}:${input.startDate || ''}:${input.endDate || ''}`
  )
  const scale = input.locationId ? 1 : 6 // brand view aggregates several locations
  return {
    __typename: 'DashboardKpisResponse',
    activeMembers: faker.number.int({ min: 220, max: 540 }) * scale,
    newMembers: faker.number.int({ min: 18, max: 46 }) * scale,
    newMembersPrevious: faker.number.int({ min: 14, max: 40 }) * scale,
    totalVisits: faker.number.int({ min: 1400, max: 2600 }) * scale,
    totalVisitsPrevious: faker.number.int({ min: 1200, max: 2400 }) * scale,
    totalRevenue: round2(faker.number.float({ min: 28000, max: 52000 }) * scale),
    totalRevenuePrevious: round2(faker.number.float({ min: 24000, max: 49000 }) * scale),
    currency: 'USD',
  }
}

// Root-field name -> generator(argsObject). Generic resolver handles everything else.
export const CURATED = {
  createPayments: genCreatePayments,
  calendarSessions: genCalendarSessions,
  sessions: genCalendarSessions,
  weekViewSessions: genWeekViewSessions,
  availableReports: genAvailableReports,
  reportDefinition: genReportDefinition,
  runReport: genRunReport,
  calculateCart: genCalculateCart,
  getDashboardKpis: genDashboardKpis,
}
